import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { toast } from 'sonner';
import {
  Plus,
  Database as DatabaseIcon,
  Key,
  RefreshCw,
  Pencil,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { DatabaseRowEditor } from './DatabaseRowEditor';

interface DatabaseColumn {
  name: string;
  type: string;
  nullable: boolean;
  defaultValue: string | null;
}

interface TableRowsData {
  rows: Record<string, unknown>[];
  totalCount: number;
  primaryKey: string | null;
}

function formatCellValue(value: unknown, nullLabel: string): string {
  if (value === null) return nullLabel;
  if (value === undefined) return '';
  if (typeof value === 'object') {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return JSON.stringify(value);
  }
  return String(value);
}

interface DatabaseTableBrowserProps {
  databaseId: string;
  tableName: string;
  apiBasePath: string;
}

export function DatabaseTableBrowser({
  databaseId,
  tableName,
  apiBasePath,
}: DatabaseTableBrowserProps) {
  const { t } = useTranslation('databases');
  const [columns, setColumns] = useState<DatabaseColumn[]>([]);
  const [tableData, setTableData] = useState<TableRowsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const [addRowOpen, setAddRowOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<Record<string, unknown> | null>(
    null,
  );
  const [deleteRow, setDeleteRow] = useState<Record<string, unknown> | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(
    async (pageNum: number) => {
      setLoading(true);
      try {
        const [columnsRes, rowsRes] = await Promise.all([
          fetch(`${apiBasePath}/tables/${encodeURIComponent(tableName)}`, {
            credentials: 'include',
          }),
          fetch(
            `${apiBasePath}/tables/${encodeURIComponent(tableName)}/rows?limit=${pageSize}&offset=${pageNum * pageSize}`,
            { credentials: 'include' },
          ),
        ]);

        if (columnsRes.ok) {
          const colData = await columnsRes.json();
          setColumns(colData.data);
        }
        if (rowsRes.ok) {
          const rowData = await rowsRes.json();
          setTableData(rowData.data);
        }
      } catch {
        toast.error(t('errors.loadTableData'));
      } finally {
        setLoading(false);
      }
    },
    [apiBasePath, tableName, pageSize, t],
  );

  useEffect(() => {
    setPage(0);
    fetchData(0);
  }, [tableName, fetchData]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handleDeleteRow = async () => {
    if (!deleteRow || !tableData?.primaryKey) return;
    setDeleting(true);
    try {
      const pkValue = deleteRow[tableData.primaryKey];
      const response = await fetch(
        `${apiBasePath}/tables/${encodeURIComponent(tableName)}/rows/${encodeURIComponent(String(pkValue))}?primaryKey=${encodeURIComponent(tableData.primaryKey)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (response.ok) {
        setDeleteRow(null);
        fetchData(page);
      } else {
        toast.error(t('errors.deleteRow'));
      }
    } catch {
      toast.error(t('errors.deleteRow'));
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = tableData ? Math.ceil(tableData.totalCount / pageSize) : 0;

  if (loading) {
    return (
      <div className="mt-6 border-t pt-6 flex items-center justify-center py-12">
        <Spinner className="h-8 w-8" />
        <span className="ml-3 text-sm text-muted-foreground">
          {t('loading.data')}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-6 border-t pt-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold flex items-center">
          {t('tables.data', { table: tableName })}
          {tableData && (
            <span className="ml-2 text-muted-foreground font-normal">
              {t('tables.rowCount', { count: tableData.totalCount })}
            </span>
          )}
        </h4>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fetchData(page)}
            aria-label={t('tables.refreshData')}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          {tableData?.primaryKey && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddRowOpen(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('actions.addRow')}
            </Button>
          )}
        </div>
      </div>

      {tableData && tableData.rows.length > 0 ? (
        <>
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  {columns.map((col) => (
                    <TableHead key={col.name} className="text-xs">
                      <div className="flex items-center gap-1">
                        <span>{col.name}</span>
                        {col.name === tableData.primaryKey && (
                          <Key className="w-3 h-3 text-warning" />
                        )}
                      </div>
                      <span className="text-xs font-normal text-muted-foreground">
                        {col.type}
                      </span>
                    </TableHead>
                  ))}
                  {tableData.primaryKey && (
                    <TableHead className="text-right text-xs">
                      {t('tables.actions')}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((col) => (
                      <TableCell key={col.name} className="text-sm">
                        <span
                          className={`${row[col.name] === null ? 'text-muted-foreground italic' : ''} truncate block max-w-xs`}
                        >
                          {formatCellValue(row[col.name], t('tables.null'))}
                        </span>
                      </TableCell>
                    ))}
                    {tableData.primaryKey && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingRow(row)}
                          aria-label={t('actions.editRow')}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteRow(row)}
                          className="text-destructive hover:text-destructive"
                          aria-label={t('actions.deleteRow')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="py-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('tables.showing', {
                  start: page * pageSize + 1,
                  end: Math.min((page + 1) * pageSize, tableData.totalCount),
                  total: tableData.totalCount,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 0}
                  aria-label={t('actions.previous')}
                >
                  {t('actions.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages - 1}
                  aria-label={t('actions.next')}
                >
                  {t('actions.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-muted rounded-lg">
          <DatabaseIcon className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">{t('empty.noData')}</p>
          {tableData?.primaryKey && (
            <Button
              variant="ghost"
              onClick={() => setAddRowOpen(true)}
              className="mt-3"
            >
              {t('actions.addFirstRow')}
            </Button>
          )}
        </div>
      )}

      {!tableData?.primaryKey && tableData && tableData.rows.length > 0 && (
        <p className="mt-2 text-xs text-warning flex items-center">
          <AlertTriangle className="w-4 h-4 mr-1" />
          {t('tables.noPrimaryKey')}
        </p>
      )}

      {addRowOpen && columns.length > 0 && (
        <DatabaseRowEditor
          databaseId={databaseId}
          tableName={tableName}
          columns={columns}
          primaryKey={tableData?.primaryKey ?? null}
          open={addRowOpen}
          onOpenChange={setAddRowOpen}
          onSaved={() => fetchData(page)}
          apiBasePath={apiBasePath}
        />
      )}

      {editingRow && columns.length > 0 && (
        <DatabaseRowEditor
          databaseId={databaseId}
          tableName={tableName}
          columns={columns}
          row={editingRow}
          primaryKey={tableData?.primaryKey ?? null}
          open={!!editingRow}
          onOpenChange={(open) => {
            if (!open) setEditingRow(null);
          }}
          onSaved={() => fetchData(page)}
          apiBasePath={apiBasePath}
        />
      )}

      <ConfirmDialog
        open={!!deleteRow}
        onOpenChange={(open) => {
          if (!open) setDeleteRow(null);
        }}
        title={t('deleteDialog.rowTitle')}
        description={t('deleteDialog.rowDescription')}
        confirmLabel={t('actions.delete')}
        cancelLabel={t('common:cancel')}
        onConfirm={handleDeleteRow}
        loading={deleting}
      />
    </div>
  );
}
